// Base URL for translations on CloudFront/S3.
// Configure via Vite env or a runtime-injected global config.
// Note: We read this dynamically in ensureBaseUrl() rather than as a constant
// because window.RUNTIME_CONFIG may be set after module load.
function getTranslationsBaseUrl() {
    // Vite env (per app): VITE_TRANSLATIONS_BASE_URL
    if (import.meta.env?.VITE_TRANSLATIONS_BASE_URL) {
        return import.meta.env.VITE_TRANSLATIONS_BASE_URL;
    }
    // Optional runtime config attached to window by the shell
    if (typeof window !== "undefined") {
        const runtimeConfig = window.RUNTIME_CONFIG;
        if (runtimeConfig?.translationsBaseUrl) {
            return runtimeConfig.translationsBaseUrl;
        }
    }
    return undefined;
}
function ensureBaseUrl() {
    const baseUrl = getTranslationsBaseUrl();
    if (!baseUrl) {
        throw new Error("TRANSLATIONS_BASE_URL is not configured. Set VITE_TRANSLATIONS_BASE_URL or window.RUNTIME_CONFIG.translationsBaseUrl.");
    }
    return baseUrl;
}
async function fetchJson(url) {
    const res = await fetch(url, {
        credentials: "omit",
    });
    if (!res.ok) {
        throw new Error(`Failed to fetch ${url} (${res.status})`);
    }
    return (await res.json());
}
/**
 * Fetches the translation manifest from the CDN.
 */
export async function fetchManifest() {
    const baseUrl = ensureBaseUrl();
    return fetchJson(`${baseUrl}/manifest.json`);
}
/**
 * Downloads all translation files for a language and merges them.
 *
 * @param languageManifest - Manifest entries for a single language
 * @param language - Language code used to construct the URL path
 * @returns Merged translations object with flattened keys
 */
export async function fetchTranslationsForLanguage(languageManifest, language) {
    const baseUrl = ensureBaseUrl();
    const entries = Object.entries(languageManifest);
    const translationPromises = entries.map(async ([chunkName, entry]) => {
        if (!entry?.filename) {
            console.warn(`Invalid manifest entry for chunk "${chunkName}": missing filename`);
            return {};
        }
        const url = `${baseUrl}/${language}/${entry.filename}`;
        return fetchJson(url);
    });
    const chunks = await Promise.all(translationPromises);
    return Object.assign({}, ...chunks);
}
/**
 * Loads translations for a specific language from the CDN.
 *
 * @param language - Language code (e.g., 'en')
 * @returns Merged translations object for the specified language
 */
export async function loadRemoteTranslationsForLanguage(language) {
    if (typeof window === "undefined") {
        // SSR / Node: fall back to empty translations
        return {};
    }
    const baseUrl = ensureBaseUrl();
    // If the base URL points directly at a JSON file, it's likely language-agnostic
    // or we can't determine the language structure, so return all translations
    if (baseUrl.toLowerCase().endsWith(".json")) {
        return fetchJson(baseUrl);
    }
    // Fetch the root manifest and extract the language-specific entries
    const rootManifest = await fetchManifest();
    const languageManifest = rootManifest[language];
    if (!languageManifest) {
        console.warn(`No translations found for language: ${language}`);
        return {};
    }
    return fetchTranslationsForLanguage(languageManifest, language);
}
/**
 * Loads translations from the CDN using either:
 * - A single JSON endpoint (when TRANSLATIONS_BASE_URL ends with .json), or
 * - A manifest + hashed chunks (default).
 *
 * Returns a merged translations object suitable for passing as
 * i18next resources for a single language (e.g., { translation: data }).
 *
 * @deprecated Use loadRemoteTranslationsForLanguage instead for language-specific loading
 */
export async function loadRemoteTranslations() {
    if (typeof window === "undefined") {
        // SSR / Node: fall back to empty translations and let caller decide.
        return {};
    }
    const baseUrl = ensureBaseUrl();
    // Alignment with existing CDN repo (contentmanager):
    // If the base URL points directly at a JSON file (e.g., fullapp.dev.json),
    // treat it as a single translation document and skip manifest logic.
    if (baseUrl.toLowerCase().endsWith(".json")) {
        return fetchJson(baseUrl);
    }
    // Default path: load English translations
    return loadRemoteTranslationsForLanguage("en");
}
//# sourceMappingURL=remote.js.map